// The lifecycle console's wire shapes. These MIRROR src/server/routes/lifecycle.ts exactly —
// when the server shape changes, this file changes with it and `bunx tsc --noEmit` says so.
// (Same convention as api/brief.ts; no client module may import from src/server.)
import { request } from './client.js';
import type { Gate, Phase, RailStep } from './crm.js';
import type { Problem } from '../../shared/problems.js';

export type { Gate, Phase, RailStep };

export type GateSubjectKind =
  | 'requirement' | 'proposal' | 'deliverable' | 'document' | 'milestone'
  | 'defect' | 'acceptance_record' | 'change_request' | 'sow_version'
  | 'open_question' | 'operator_task' | 'portal_grant';

export interface GateSubject {
  ref: string;
  kind: GateSubjectKind;
  id: number;
  label?: string;
  /** One hop of reach: what accepting/clearing this subject would also touch. Never a promise. */
  covers?: string[];
}

export interface LifeCondition {
  label: string;
  kind: 'auto' | 'manual';
  met: boolean;
  detail: string;
  item_id?: number | null;
  note?: string | null;
  custom?: boolean;
  subjects?: GateSubject[];
  subject_overflow?: number;
  /** Absent ⇒ true. `false` means `met` is vacuous — the universe is empty. */
  applicable?: boolean;
  vacuous_reason?: string;
}

export interface LifeGate {
  gate: Gate;
  phase: Phase;
  title: string;
  conditions: LifeCondition[];
  allMet: boolean;
  metCount: number;
  total: number;
  openCount: number;
  heldCount: number;
  naCount: number;
}

export interface LifeGateRow {
  gate: Gate;
  phase: Phase;
  title: string;
  stage: number;
  allMet: boolean;
  metCount: number;
  total: number;
  openCount: number;
  heldCount: number;
  naCount: number;
  unmet: Array<{ label: string; kind: 'auto' | 'manual'; detail: string; subjects: GateSubject[] }>;
}

export interface LifeEngagement {
  id: number;
  code: string;
  name: string;
  client_id: number;
  client_name: string;
  sponsor_contact_id: number | null;
  sponsor_name: string | null;
  commercial_model: string;
  currency: string;
  estimate_value: string | null;
  kickoff_date: string | null;
  review_window_days: number;
  /** The correspondence language — everything produced inside the engagement is written in it. */
  language: string;
  archived_at: string | null;
  parent_engagement_id: number | null;
  parent_code: string | null;
  parent_name: string | null;
  portal_enabled: boolean;
  current_phase: Phase;
  phase_label: string;
  /** 1-based. `phase_step` of `phase_count` — never `indexOf`. */
  phase_step: number;
  phase_count: number;
  gate: Gate;
  gate_title: string;
}

/** What a forced crossing left behind, as the snapshot recorded it. */
export interface SkippedGate {
  gate: Gate;
  title: string;
  phase: Phase;
  metCount: number;
  total: number;
  unmet: Array<{ label: string; kind: 'auto' | 'manual'; detail: string }>;
}

export interface LifeTransition {
  id: number;
  from_phase: Phase;
  to_phase: Phase;
  forced: boolean;
  override_reason: string | null;
  gate_snapshot: {
    gate: Gate;
    title: string;
    allMet: boolean;
    metCount: number;
    total: number;
    conditions: Array<{ label: string; kind: 'auto' | 'manual'; met: boolean; detail: string }>;
    evaluated_at: string;
    gates_skipped?: SkippedGate[];
  } | null;
  created_at: string;
  created_by: number | null;
  actor_name: string | null;
}

export interface LifeState {
  generated_at: string;
  problems: Problem[];
  engagement: LifeEngagement;
  rail: RailStep[];
  /** null when the gate engine refused — the phase survives, the conditions do not. */
  gate: LifeGate | null;
  gates: LifeGateRow[];
  transitions: LifeTransition[];
  source: string;
}

export interface LifeGateReply {
  generated_at: string;
  problems: Problem[];
  gate: LifeGate | null;
  source: string;
}

export const getLifecycleState = (engagementId: number) =>
  request<LifeState>(`/lifecycle/${engagementId}/state`);

export const getLifecycleGate = (engagementId: number, gate: Gate) =>
  request<LifeGateReply>(`/lifecycle/${engagementId}/gates/${gate}`);

/** The endpoint a gate panel prints in its foot, so the number always names its source. */
export const gateSource = (engagementId: number, gate: Gate) =>
  `GET /api/lifecycle/${engagementId}/gates/${gate}`;
